import { useEffect, useMemo, useState } from "react";
import { fetchSheetViaProxy } from "../../lib/sheets";

const SPECIALX_SHEET_ID = "1bdWoKQm-B96F6fGmeB7Lo0BAiKDglXsQboXaVdtkIZI";
const RANGE = "Standings_Combined!A1:AZ100";

const isNumeric = (v) => v !== null && v !== "" && !isNaN(Number(v));

function computeTop3ByRaceIndices(rowsAsArrays, raceIndices) {
    const map = new Map();

    for (const idx of raceIndices) {
        const nums = rowsAsArrays
            .map((arr) => arr[idx])
            .filter((v) => v !== null && v !== undefined && v !== "")
            .map((v) => Number(v))
            .filter((v) => !Number.isNaN(v));

        if (!nums.length) {
            map.set(idx, null);
            continue;
        }

        const uniqDesc = Array.from(new Set(nums)).sort((a, b) => b - a);
        map.set(idx, {
            gold: uniqDesc[0] ?? null,
            silver: uniqDesc[1] ?? null,
            bronze: uniqDesc[2] ?? null,
        });
    }

    return map;
}

export default function StandingsSpecialX() {
    const [data, setData] = useState({ header: [], rows: [] });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [noData, setNoData] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const res = await fetchSheetViaProxy({
                    range: RANGE,
                    sheetId: SPECIALX_SHEET_ID,
                });
                setData(res);
            } catch (e) {
                const msg = e?.message || String(e);

                const isNoStandingsYet =
                    msg.includes("Unable to parse range") ||
                    msg.includes("Upstream error") ||
                    msg.includes('"status": 400') ||
                    msg.includes("Standings_Combined");

                if (isNoStandingsYet) {
                    setNoData(true);
                    setError("");
                } else {
                    setError(msg);
                }
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const {
        visibleIndices,
        visibleHeaders,
        rowsAsArrays,
        top3PerRaceColumn,
        completedRuns,
        idxFlagUrl,
        idxAutoUrl,
        idxDriver,
        idxPoints,
        idxDiff,
        idxDelta,
        idxPos,
        idxCarNum,
        idxFlagPlaceholder,
        idxAutoPlaceholder,
        raceIndices,
        dropIndices,
    } = useMemo(() => {
        const headers = data.header || [];
        const rowsAsArrays = (data.rows || []).map((r) => headers.map((h) => r[h]));

        const idxPos = headers.indexOf("Position");
        const idxDelta = headers.indexOf("Δ");
        const idxFlagPlaceholder = 2; // C
        const idxCarNum = headers.indexOf("Fahrzeugnummer");
        const idxAutoPlaceholder = 4; // E
        const idxDriver = headers.indexOf("Fahrername");
        const idxPoints = headers.indexOf("Points");
        const idxDiff = headers.indexOf("Diff");

        const idxFlagUrl = headers.indexOf("FlagURL");
        const idxAutoUrl = headers.indexOf("AutoURL");

        const raceIndices = headers
            .map((h, i) => ({ h: String(h || "").trim(), i }))
            .filter(({ h }) => /^R\d+(?:[A-Z]\d*)?$/i.test(h))
            .map(({ i }) => i);

        const dropIndices = headers
            .map((h, i) => ({ h: String(h || "").trim(), i }))
            .filter(({ h }) => /^Drop\d+$/i.test(h))
            .map(({ i }) => i);

        const visibleIndices = headers
            .map((h, i) => ({ h: String(h || "").trim(), i }))
            .filter(({ h, i }) => {
                if (h.startsWith("@")) return false;
                if (["Flag", "Auto", "FlagURL", "AutoURL", "TeamURL"].includes(h)) return false;

                return (
                    i === idxPos ||
                    i === idxDelta ||
                    i === idxFlagPlaceholder ||
                    i === idxCarNum ||
                    i === idxAutoPlaceholder ||
                    i === idxDriver ||
                    i === idxPoints ||
                    i === idxDiff ||
                    raceIndices.includes(i) ||
                    dropIndices.includes(i)
                );
            })
            .map(({ i }) => i);

        const visibleHeaders = visibleIndices.map((i) => headers[i]);
        const top3PerRaceColumn = computeTop3ByRaceIndices(rowsAsArrays, raceIndices);

        let completedRuns = 0;
        for (const idx of raceIndices) {
            const anyNumberInCol = rowsAsArrays.some((row) => {
                const v = row[idx];
                return v !== null && v !== "" && !Number.isNaN(Number(v));
            });
            if (anyNumberInCol) completedRuns++;
        }

        return {
            visibleIndices,
            visibleHeaders,
            rowsAsArrays,
            top3PerRaceColumn,
            completedRuns,
            idxFlagUrl,
            idxAutoUrl,
            idxDriver,
            idxPoints,
            idxDiff,
            idxDelta,
            idxPos,
            idxCarNum,
            idxFlagPlaceholder,
            idxAutoPlaceholder,
            raceIndices,
            dropIndices,
        };
    }, [data]);

    if (loading) return <div className="card"><p>Lade Standings...</p></div>;

    if (noData) {
        return (
            <div className="card">
                <h2>Standings <span className="pill pill--gesamt">Gesamt</span></h2>
                <p className="muted">
                    Es hat noch kein Rennen dieser Serie stattgefunden.
                    Sobald das erste Rennen gefahren und ausgewertet wurde, erscheinen hier die Gesamt-Standings der Special Xperience.
                </p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="card">
                <p style={{ color: "#f55" }}>Fehler: {error}</p>
            </div>
        );
    }

    return (
        <>
            <div className="card">
                <h2>Standings <span className="pill pill--gesamt">Gesamt</span></h2>
                <p className="muted">
                    Gesamtwertung nach Abschluss des {completedRuns}. Laufes präsentiert von BavarianSimTec.
                </p>
            </div>

            <div className="fancyTable">
                <table className="tbl">
                    <thead>
                    <tr>
                        {visibleHeaders.map((h, i) => {
                            const actualIndex = visibleIndices[i];

                            const label =
                                actualIndex === idxPos
                                    ? "Pos"
                                    : actualIndex === idxCarNum
                                        ? "#"
                                        : h;

                            const isRace = raceIndices.includes(actualIndex);
                            const isDrop = dropIndices.includes(actualIndex);

                            const raceOrder = raceIndices.indexOf(actualIndex);
                            const raceLeft = raceOrder === 0 && isRace;
                            const raceRight = raceOrder === raceIndices.length - 1 && isRace;
                            const evenStripe = isRace && (raceOrder % 2 === 0);

                            const leftAligned =
                                actualIndex === idxPos ||
                                actualIndex === idxDriver;

                            const thClass = [
                                isRace ? "race-col" : "",
                                isDrop ? "drop-col" : "",
                                raceLeft ? "race-left" : "",
                                raceRight ? "race-right" : "",
                                evenStripe ? "race-alt" : "",
                                (actualIndex === idxFlagPlaceholder || actualIndex === idxAutoPlaceholder) ? "icon" : "",
                                leftAligned ? "align-left" : "",
                            ].filter(Boolean).join(" ");

                            return (
                                <th key={i} className={thClass}>
                                    {label}
                                </th>
                            );
                        })}
                    </tr>
                    </thead>

                    <tbody>
                    {rowsAsArrays.map((arr, ri) => (
                        <tr key={ri}>
                            {visibleIndices.map((actualIndex, i) => {
                                const val = arr[actualIndex];

                                if (actualIndex === idxFlagPlaceholder) {
                                    const src = idxFlagUrl >= 0 ? arr[idxFlagUrl] || "" : "";
                                    return (
                                        <td key={i} className="icon">
                                            {src ? <img src={src} alt="Flag" className="flag lg" loading="lazy" /> : null}
                                        </td>
                                    );
                                }

                                if (actualIndex === idxAutoPlaceholder) {
                                    const src = idxAutoUrl >= 0 ? arr[idxAutoUrl] || "" : "";
                                    return (
                                        <td key={i} className="icon">
                                            {src ? <img src={src} alt="Car" className="carlogo lg" loading="lazy" /> : null}
                                        </td>
                                    );
                                }

                                const isRace = raceIndices.includes(actualIndex);
                                const isDrop = dropIndices.includes(actualIndex);

                                const raceOrder = raceIndices.indexOf(actualIndex);
                                const raceLeft = raceOrder === 0 && isRace;
                                const raceRight = raceOrder === raceIndices.length - 1 && isRace;
                                const evenStripe = isRace && (raceOrder % 2 === 0);

                                let tierClass = "";
                                if (isRace && val !== "" && val !== null && val !== undefined) {
                                    const numVal = Number(val);
                                    const t3 = top3PerRaceColumn.get(actualIndex);
                                    if (t3 && !Number.isNaN(numVal)) {
                                        if (numVal === t3.gold) tierClass = "race-gold";
                                        else if (numVal === t3.silver) tierClass = "race-silver";
                                        else if (numVal === t3.bronze) tierClass = "race-bronze";
                                    }
                                }

                                const leftAligned =
                                    actualIndex === idxPos ||
                                    actualIndex === idxDriver ||
                                    actualIndex === idxDiff;

                                let deltaClass = "";
                                if (actualIndex === idxDelta) {
                                    const n = Number(val);
                                    if (!Number.isNaN(n)) {
                                        if (n > 0) deltaClass = "delta-up";
                                        else if (n < 0) deltaClass = "delta-down";
                                    }
                                }

                                let statusClass = "";
                                if (isRace) {
                                    const s = String(val ?? "").trim().toUpperCase();
                                    if (s === "DNS") statusClass = "status-dns";
                                    else if (s === "DNF") statusClass = "status-dnf";
                                    else if (s === "BAN") statusClass = "status-ban";
                                }

                                const numeric =
                                    isNumeric(val) ||
                                    isRace ||
                                    isDrop ||
                                    actualIndex === idxPos ||
                                    actualIndex === idxDelta ||
                                    actualIndex === idxCarNum ||
                                    actualIndex === idxPoints ||
                                    actualIndex === idxDiff;

                                const tdClass = [
                                    numeric ? "num" : "",
                                    isRace ? "race-col" : "",
                                    isDrop ? "drop-col" : "",
                                    raceLeft ? "race-left" : "",
                                    raceRight ? "race-right" : "",
                                    evenStripe ? "race-alt" : "",
                                    tierClass,
                                    leftAligned ? "align-left" : "",
                                    actualIndex === idxPoints ? "points-highlight" : "",
                                    actualIndex === idxPos ? "pos-highlight" : "",
                                    deltaClass,
                                    statusClass,
                                ].filter(Boolean).join(" ");

                                return (
                                    <td key={i} className={tdClass}>
                                        {val}
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>
        </>
    );
}